// The trail above a page (the wireframe's `.crumbs`), on in-app routes.
//
// AGE draws MUD's chevron separator only when the crumbs arrive as the `items`
// property. Nested `<mud-breadcrumb-item>` children fall back to a literal "›"
// with no room around it, which is what every page here drew. The crumbs'
// anchors live in a shadow root, so an unmodified primary click is routed the
// way RouterLink routes AGE's link; a click with a modifier held still reaches
// the browser's anchor and opens where the reader asked.
import { useRef, type ComponentProps } from 'react';
import { useNavigate } from 'react-router-dom';
import { MudBreadcrumb } from '@egov-moldova/mud-react';

export interface Crumb {
  label: string;
  /** The route. The last crumb is the current page and never links. */
  to?: string | undefined;
}

export interface BreadcrumbProps {
  crumbs: Crumb[];
  /** The landmark's accessible name. */
  label: string;
  maxVisible?: number | undefined;
}

type Items = NonNullable<ComponentProps<typeof MudBreadcrumb>['items']>;

export function Breadcrumb({ crumbs, label, maxVisible }: BreadcrumbProps): JSX.Element {
  const navigate = useNavigate();
  // `mudSelect` carries no pointer state, so the click that caused it is read
  // on its way down, before AGE's anchor handler emits the event.
  const modified = useRef(false);

  const items: Items = crumbs.map((crumb, index) => {
    if (index === crumbs.length - 1) return { label: crumb.label, active: true };
    return crumb.to ? { label: crumb.label, href: crumb.to } : { label: crumb.label };
  });

  return (
    <MudBreadcrumb
      items={items}
      label={label}
      {...(maxVisible ? { maxVisible } : {})}
      onClickCapture={(event) => {
        modified.current =
          event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
      }}
      onMudSelect={(event) => {
        const href = event.detail.href;
        if (!href || modified.current) return;
        event.preventDefault();
        navigate(href);
      }}
    />
  );
}
